import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './Navigation.css';
import Avatar from './ui/Avatar';
import Button from './ui/Button';
import StatusBanner from './ui/StatusBanner';
import NotificationsBell from './notifications/NotificationsBell';
import { useTerminology } from '../context/TenantConfigContext';
import {
  getScopeRollupLabel,
  getTierLabel,
  getUserDisplayName,
  getUserReferenceTitle,
  getWorkspaceForUser
} from '../config/appRoutes';
import { COMPANY_NAME, PRODUCT_NAME } from '../config/appBranding';
import { removeUserAvatar, uploadUserAvatar } from '../services/avatarApi';

const AVATAR_CROP_PREVIEW_SIZE = 180;
const AVATAR_CROP_OUTPUT_SIZE = 512;
// Top nav is intentionally executive-thin: only the three highest-cadence
// surfaces stay visible alongside the grouped "Menu" popover. Everything
// else (Requests, People & Talent, Performance, Setup, Admin) lives in
// the Menu — see groupedNavigation rendering below.
const PREFERRED_PRIMARY_NAV_PATHS = [
  '/today',
  '/executive',
  '/alerts'
];

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Unable to read image file.'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Unable to load image preview.'));
    image.src = src;
  });
}

function isNavItemActive(currentPath, itemPath) {
  if (currentPath === itemPath) return true;
  const path = String(currentPath || '');
  if (
    (path.startsWith('/employees/profile/') || path.startsWith('/workforce/employees/'))
    && (itemPath === '/employees' || itemPath === '/workforce')
  ) return true;
  return false;
}

async function createCroppedAvatarFile({ imageSrc, offset, zoom }) {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_CROP_OUTPUT_SIZE;
  canvas.height = AVATAR_CROP_OUTPUT_SIZE;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Unable to prepare cropped avatar.');
  }
  const scale = Math.max(
    AVATAR_CROP_OUTPUT_SIZE / image.naturalWidth,
    AVATAR_CROP_OUTPUT_SIZE / image.naturalHeight
  ) * zoom;
  const previewToOutput = AVATAR_CROP_OUTPUT_SIZE / AVATAR_CROP_PREVIEW_SIZE;
  const drawWidth = image.naturalWidth * scale;
  const drawHeight = image.naturalHeight * scale;
  const dx = (AVATAR_CROP_OUTPUT_SIZE - drawWidth) / 2 + offset.x * previewToOutput;
  const dy = (AVATAR_CROP_OUTPUT_SIZE - drawHeight) / 2 + offset.y * previewToOutput;
  context.drawImage(image, dx, dy, drawWidth, drawHeight);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.9));
  if (!blob) {
    throw new Error('Unable to prepare cropped avatar.');
  }
  return new File([blob], 'avatar.webp', { type: 'image/webp' });
}

function Navigation({
  activeAuthorityLabel = '',
  activeScopeLabel = '',
  badgeCounts = {},
  currentPath,
  navItems = [],
  navGroups = [],
  onExitClientPortal = null,
  onLogout,
  onNavigate,
  onOpenPasswordPanel,
  onRefreshCurrentUser,
  runtimeVersion,
  user
}) {
  const { companyDisplayName, formatWorkspaceLabel } = useTerminology();
  const tierLabel = user ? getTierLabel(user) : null;
  const userDisplayName = getUserDisplayName(user);
  const userReferenceTitle = getUserReferenceTitle(user);
  const workspaceCompanyName = companyDisplayName || user?.companyName || COMPANY_NAME || PRODUCT_NAME;
  const workspace = user ? getWorkspaceForUser(user) : null;
  const workspaceLabel = workspace ? formatWorkspaceLabel(workspace) : '';
  const rollupLabel = user ? getScopeRollupLabel(user) : '';
  const resolvedScopeLabel = activeScopeLabel || rollupLabel || '';
  const versionLabel = runtimeVersion?.commit
    ? `Commit ${String(runtimeVersion.commit).slice(0, 7)}`
    : (runtimeVersion?.version ? `Version ${runtimeVersion.version}` : '');

  const groupedNavigation = useMemo(() => (
    navGroups.length
      ? navGroups
      : (navItems.length ? [{ label: '', items: navItems }] : [])
  ), [navGroups, navItems]);
  const navigationEntries = useMemo(() => (
    groupedNavigation.flatMap((group, groupIndex) => (
      (group.items || []).map((item, itemIndex) => ({
        ...item,
        groupLabel: group.label || '',
        navKey: `${group.label || `group-${groupIndex}`}:${item.path}:${itemIndex}`
      }))
    ))
  ), [groupedNavigation]);
  const primaryNavigationEntries = useMemo(() => {
    // Strict allowlist: only the three preferred top-nav routes ever
    // appear in the visible bar. Every other route — even if the role
    // has more navigation items than the preferred list — is pushed
    // into the grouped "Menu" popover for an executive-thin shell.
    const preferredEntries = PREFERRED_PRIMARY_NAV_PATHS
      .map(path => navigationEntries.find(item => item.path === path))
      .filter(Boolean);
    return preferredEntries.length ? preferredEntries : navigationEntries.slice(0, 1);
  }, [navigationEntries]);
  const primaryNavigationPaths = useMemo(
    () => new Set(primaryNavigationEntries.map(item => item.path)),
    [primaryNavigationEntries]
  );
  const isMoreActive = useMemo(
    () => navigationEntries.some(item => !primaryNavigationPaths.has(item.path) && isNavItemActive(currentPath, item.path)),
    [currentPath, navigationEntries, primaryNavigationPaths]
  );
  const hasMenuNavigation = groupedNavigation.some(group => (group.items || []).length > 0);

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState('');
  const [cropImageSrc, setCropImageSrc] = useState('');
  const [cropZoom, setCropZoom] = useState(1);
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState(null);
  const fileInputRef = useRef(null);
  const profileModalRef = useRef(null);
  const navScrollRef = useRef(null);
  const activeNavRef = useRef(null);
  const moreMenuRef = useRef(null);

  useEffect(() => () => {
    if (cropImageSrc && cropImageSrc.startsWith('blob:')) {
      URL.revokeObjectURL(cropImageSrc);
    }
  }, [cropImageSrc]);

  useEffect(() => {
    if (!isProfileOpen) return undefined;
    const onEsc = (event) => {
      if (event.key === 'Escape') setIsProfileOpen(false);
    };
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('keydown', onEsc);
    };
  }, [isProfileOpen]);

  useEffect(() => {
    activeNavRef.current?.scrollIntoView?.({
      block: 'nearest',
      inline: 'center',
      behavior: 'smooth'
    });
  }, [currentPath, primaryNavigationEntries.length]);

  useEffect(() => {
    if (!isMoreOpen) return undefined;
    // Close on click outside the More menu container. We listen on `click`
    // (not `mousedown`) so menu-item clicks can complete their own navigate
    // handler before the menu unmounts; we also guard with the menu ref
    // so DOM detail (e.g. an item being unmounted mid-event) can't leave
    // the popover stuck open.
    const onDocumentClick = (event) => {
      const root = moreMenuRef.current;
      if (root && root.contains(event.target)) return;
      setIsMoreOpen(false);
    };
    const onEsc = (event) => {
      if (event.key === 'Escape') setIsMoreOpen(false);
    };
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [isMoreOpen]);

  const closeProfile = () => {
    setIsProfileOpen(false);
    setAvatarError('');
    setCropImageSrc('');
    setCropZoom(1);
    setCropOffset({ x: 0, y: 0 });
    setDragStart(null);
  };

  const handleNavigate = (path) => {
    setIsMoreOpen(false);
    onNavigate(path);
  };

  const handleChooseAvatar = () => {
    setAvatarError('');
    fileInputRef.current?.click();
  };

  const handleAvatarFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !user?.id) return;
    setAvatarError('');
    try {
      const previewSrc = await readFileAsDataUrl(file);
      if (cropImageSrc && cropImageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(cropImageSrc);
      }
      setCropImageSrc(previewSrc);
      setCropZoom(1);
      setCropOffset({ x: 0, y: 0 });
    } catch (error) {
      setAvatarError(error.message || "Couldn't preview that photo. Try a different format.");
    }
  };

  const handleCropPointerDown = (event) => {
    if (!cropImageSrc || isUploadingAvatar) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragStart({
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offset: cropOffset
    });
  };

  const handleCropPointerMove = (event) => {
    if (!dragStart || dragStart.pointerId !== event.pointerId) return;
    setCropOffset({
      x: clamp(dragStart.offset.x + event.clientX - dragStart.startX, -70, 70),
      y: clamp(dragStart.offset.y + event.clientY - dragStart.startY, -70, 70)
    });
  };

  const handleCropPointerUp = (event) => {
    if (dragStart?.pointerId === event.pointerId) {
      setDragStart(null);
    }
  };

  const handleSaveCroppedAvatar = async () => {
    if (!cropImageSrc || !user?.id) return;
    setIsUploadingAvatar(true);
    setAvatarError('');
    try {
      const croppedFile = await createCroppedAvatarFile({
        imageSrc: cropImageSrc,
        offset: cropOffset,
        zoom: cropZoom
      });
      await uploadUserAvatar(user.id, croppedFile);
      await onRefreshCurrentUser?.();
      setAvatarVersion(String(Date.now()));
      setCropImageSrc('');
      setCropZoom(1);
      setCropOffset({ x: 0, y: 0 });
    } catch (error) {
      setAvatarError(error.message || "Couldn't upload — try a smaller file or a different format.");
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleCancelCrop = () => {
    setCropImageSrc('');
    setCropZoom(1);
    setCropOffset({ x: 0, y: 0 });
    setDragStart(null);
  };

  const handleRemoveAvatar = async () => {
    if (!user?.id) return;
    setIsUploadingAvatar(true);
    setAvatarError('');
    try {
      await removeUserAvatar(user.id);
      await onRefreshCurrentUser?.();
      setAvatarVersion(String(Date.now()));
    } catch (error) {
      setAvatarError(error.message || "Couldn't remove the photo. Try again.");
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  return (
    <nav className="navbar">
      <div className="navbar-row">
        <div
          className="navbar-brand"
          aria-label={`${workspaceCompanyName} ${PRODUCT_NAME}`}
        >
          <div className="brand-icon" aria-hidden="true">
            <i className="fas fa-chart-line"></i>
          </div>
          <div className="brand-text">
            <span className="navbar-title">{workspaceCompanyName}</span>
            <span className="navbar-divider" aria-hidden="true">·</span>
            <span className="navbar-subtitle">{PRODUCT_NAME}</span>
          </div>
        </div>

        {navigationEntries.length > 0 && (
          <div className="navbar-links" role="navigation" aria-label="Primary" ref={navScrollRef}>
            {primaryNavigationEntries.map((item) => (
              (() => {
                const isActive = isNavItemActive(currentPath, item.path);
                return (
              <button
                key={item.navKey}
                ref={isActive ? activeNavRef : null}
                className={`navbar-link ${isActive ? 'active' : ''}`}
                onClick={() => handleNavigate(item.path)}
                title={item.groupLabel ? `${item.groupLabel} · ${item.label}` : item.label}
              >
                <span className="navbar-link-label">{item.label}</span>
                {badgeCounts[item.path] > 0 && (
                  <span
                    className="navbar-link-badge"
                    aria-label={`${badgeCounts[item.path]} pending`}
                  >
                    {badgeCounts[item.path]}
                  </span>
                )}
              </button>
                );
              })()
            ))}
            {hasMenuNavigation ? (
              <div className="navbar-more-menu navbar-menu" ref={moreMenuRef}>
                <button
                  type="button"
                  className={`navbar-link navbar-more-trigger ${isMoreOpen || isMoreActive ? 'active' : ''}`}
                  onClick={(event) => {
                    // Prevent the document-level click listener from
                    // immediately closing the menu we just toggled open.
                    event.stopPropagation();
                    setIsMoreOpen(current => !current);
                  }}
                  aria-haspopup="menu"
                  aria-expanded={isMoreOpen}
                  data-testid="navbar-more-trigger"
                >
                  <span className="navbar-link-label">Menu</span>
                  <i className="fas fa-chevron-down user-chip-caret" aria-hidden="true"></i>
                </button>
                {isMoreOpen ? (
                  // Render the popover via a portal to document.body so
                  // it isn't clipped by `.navbar-links`'s `mask-image`
                  // gradient (mask-image clips ALL descendants, including
                  // position:fixed ones, in the paint pass — that's why
                  // the popover appeared "stuck closed" in production).
                  typeof document !== 'undefined'
                    ? ReactDOM.createPortal(
                        <div
                          className="navbar-more-popover navbar-menu"
                          role="menu"
                          data-testid="navbar-more-popover"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {groupedNavigation.map(group => (
                            <section className="navbar-more-section" key={group.label || 'Workspace'}>
                              {group.label ? <div className="navbar-more-heading">{group.label}</div> : null}
                              {(group.items || []).map(item => (
                                <button
                                  key={`more-${group.label || 'group'}-${item.path}`}
                                  type="button"
                                  className={`navbar-more-item ${isNavItemActive(currentPath, item.path) ? 'active' : ''}`}
                                  onClick={() => handleNavigate(item.path)}
                                  role="menuitem"
                                >
                                  <span>{item.label}</span>
                                  {badgeCounts[item.path] > 0 ? <strong>{badgeCounts[item.path]}</strong> : null}
                                </button>
                              ))}
                            </section>
                          ))}
                        </div>,
                        document.body
                      )
                    : null
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        {user && (
          <div className="navbar-actions">
            {/* PR-3 — workforce notifications bell. Hidden in the
                client-portal preview mode because notifications
                belong to the operator's own tenant, not the
                viewed client's. */}
            {!user.viewingClientPortal ? (
              <NotificationsBell onNavigate={onNavigate} />
            ) : null}
            <div className="user-menu">
              <button
                type="button"
                className={`user-chip ${isProfileOpen ? 'is-open' : ''}`}
                onClick={() => setIsProfileOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={isProfileOpen}
              >
                <Avatar
                  avatarUrl={user.avatarUrl}
                  email={user.email}
                  identifier={user.id || user.email}
                  name={userDisplayName}
                  size="sm"
                  userId={user.id}
                  version={avatarVersion}
                />
                <span className="user-chip-name full-text-value">{userDisplayName}</span>
                <i className="fas fa-chevron-down user-chip-caret" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        )}
      </div>
      {user && isProfileOpen ? (
        <div
          className="profile-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeProfile();
          }}
        >
          <section
            className="profile-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-modal-title"
            ref={profileModalRef}
          >
            <div className="profile-modal-header">
              <div>
                <span className="profile-modal-eyebrow">Your workspace profile</span>
                <h2 id="profile-modal-title">{userDisplayName}</h2>
                {user?.email ? <p>{user.email}</p> : null}
              </div>
              <button type="button" className="profile-modal-close" onClick={closeProfile} aria-label="Close profile">
                ×
              </button>
            </div>
            <div className="profile-modal-body">
              <div className="profile-avatar-panel">
                <div className="profile-avatar-preview">
                  {cropImageSrc ? (
                    <div
                      className="profile-avatar-crop"
                      onPointerDown={handleCropPointerDown}
                      onPointerMove={handleCropPointerMove}
                      onPointerUp={handleCropPointerUp}
                      onPointerCancel={handleCropPointerUp}
                      role="img"
                      aria-label="Avatar crop preview"
                    >
                      <img
                        src={cropImageSrc}
                        alt=""
                        draggable="false"
                        style={{
                          transform: `translate(calc(-50% + ${cropOffset.x}px), calc(-50% + ${cropOffset.y}px)) scale(${cropZoom})`
                        }}
                      />
                    </div>
                  ) : (
                    <Avatar
                      avatarUrl={user.avatarUrl}
                      email={user.email}
                      identifier={user.id || user.email}
                      name={userDisplayName}
                      size="xl"
                      standalone
                      userId={user.id}
                      version={avatarVersion}
                    />
                  )}
                  {isUploadingAvatar ? <span className="profile-avatar-spinner" aria-label="Uploading photo" /> : null}
                </div>
                <div className="profile-avatar-copy">
                  <strong>Profile photo</strong>
                  <span>
                    {cropImageSrc
                      ? 'Drag the photo to reposition it, then use zoom to fit the circle.'
                      : 'Optional. Choose a photo, then crop it before saving.'}
                  </span>
                  {cropImageSrc ? (
                    <div className="profile-avatar-crop-controls">
                      <label htmlFor="avatar-zoom">Zoom</label>
                      <input
                        id="avatar-zoom"
                        type="range"
                        min="1"
                        max="2.4"
                        step="0.01"
                        value={cropZoom}
                        onChange={(event) => setCropZoom(Number(event.target.value))}
                        disabled={isUploadingAvatar}
                      />
                    </div>
                  ) : null}
                  <div className="profile-avatar-actions">
                    {cropImageSrc ? (
                      <>
                        <Button size="sm" variant="primary" onClick={handleSaveCroppedAvatar} loading={isUploadingAvatar} loadingLabel="Saving…">
                          Save photo
                        </Button>
                        <Button size="sm" variant="secondary" onClick={handleCancelCrop} disabled={isUploadingAvatar}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="secondary" onClick={handleChooseAvatar} disabled={isUploadingAvatar}>
                        Choose photo
                      </Button>
                    )}
                    {!cropImageSrc && user.avatarUrl ? (
                      <Button size="sm" variant="danger" onClick={handleRemoveAvatar} loading={isUploadingAvatar} loadingLabel="Removing…">
                        Remove photo
                      </Button>
                    ) : null}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic"
                    className="profile-avatar-input"
                    onChange={handleAvatarFileSelected}
                  />
                </div>
              </div>
              {avatarError ? <StatusBanner kind="error" message={avatarError} /> : null}
              <div className="user-menu-meta profile-modal-meta">
                {workspaceCompanyName && (
                  <div className="user-menu-row"><span>Company</span><strong>{workspaceCompanyName}</strong></div>
                )}
                {workspaceLabel && (
                  <div className="user-menu-row"><span>Your workspace</span><strong>{workspaceLabel}</strong></div>
                )}
                {userReferenceTitle && (
                  <div className="user-menu-row"><span>Title</span><strong>{userReferenceTitle}</strong></div>
                )}
                {tierLabel && (
                  <div className="user-menu-row"><span>Access</span><strong>{tierLabel}</strong></div>
                )}
                {resolvedScopeLabel && (
                  <div className="user-menu-row"><span>Scope</span><strong>{resolvedScopeLabel}</strong></div>
                )}
                {activeAuthorityLabel && (
                  <div className="user-menu-row"><span>Permissions</span><strong>{activeAuthorityLabel}</strong></div>
                )}
                {versionLabel && (
                  <div className="user-menu-row" title="Latest deployed build"><span>Build</span><strong>{versionLabel}</strong></div>
                )}
              </div>
              <div className="profile-modal-actions">
                <Button size="sm" variant="secondary" onClick={() => { closeProfile(); onOpenPasswordPanel?.(); }}>
                  <i className="fas fa-key" aria-hidden="true"></i> Change password
                </Button>
                {user?.viewingClientPortal && typeof onExitClientPortal === 'function' ? (
                  // P3: second exit affordance. The banner remains the
                  // primary surface; this entry covers the case where
                  // the banner is missed or scrolled past.
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => { closeProfile(); onExitClientPortal(); }}
                    data-testid="user-menu-exit-client-portal"
                  >
                    <i className="fas fa-arrow-right-from-bracket" aria-hidden="true"></i> Exit to VizionOps
                  </Button>
                ) : null}
                <Button size="sm" variant="danger" onClick={() => { closeProfile(); onLogout?.(); }}>
                  <i className="fas fa-right-from-bracket" aria-hidden="true"></i> Log out
                </Button>
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </nav>
  );
}

export default Navigation;
